import { Loader2 } from 'lucide-react'
import type { Ref } from 'react'

import { FOCUS_RING } from '../ui/focusRing'
import { useArmed } from './answerArming'

/**
 * One of the two answers. Same height, width and radius for both (CS-D3, Law of Similarity): the
 * filled one is the only difference, and it is not the louder of the two by size or position. Both
 * carry a 1 px border (the filled one's is transparent), so the two come out the same width as well.
 *
 * **An answer is live only once its screen has finished arriving** (`answerArming.ts`): a press in its first half second is
 * dropped, whatever sent it, and the button says so with `aria-disabled` meanwhile. That keeps the second tap of a double-tap on
 * the screen before (the summary's button, the AI step's "Agree") from answering the next question, which the person never saw.
 * A second or later click of a run (`detail` above 1, where the browser counts them) is dropped too, as a second line.
 */
export function AnswerButton({
  filled = false,
  label,
  saving,
  busy,
  onClick,
  ref,
  immediate = false,
}: {
  filled?: boolean
  label: string
  saving: boolean
  /** One of the two answers is being stored: neither takes another tap, and neither loses focus. */
  busy: boolean
  onClick: () => void
  /** For a dialog that opens with focus on this answer. */
  ref?: Ref<HTMLButtonElement>
  /**
   * Live at once, with no arming: for the safe answer a dialog opens its focus on ("Keep it"), which ends nothing, so a press on it
   * is never an answer to a question the person did not see, and a screen reader should not hear it dimmed.
   */
  immediate?: boolean
}) {
  const waited = useArmed()
  const armed = waited || immediate
  return (
    <button
      ref={ref}
      type="button"
      onClick={(event) => {
        if (!armed || event.detail > 1) return
        onClick()
      }}
      aria-disabled={busy || !armed}
      aria-busy={saving}
      // `sm:flex-1`, not `flex-1`: in the phone's column a flex basis of 0 beats the height and collapses the
      // button to its text's height (measured in a browser at 375 px: 22 px, not 48). `min-h-12`, not `h-12`,
      // so a label that wraps at a large text size grows both buttons the same way instead of being clipped.
      className={`min-h-12 rounded-xl border px-5 text-[15px] font-medium sm:flex-1 transition-[background-color,transform,opacity] duration-150 ease-out active:scale-[0.98] aria-disabled:cursor-not-allowed ${FOCUS_RING} ${
        filled
          ? 'border-transparent bg-(--text-cream) text-[#1d1a1c] not-aria-disabled:hover:bg-(--accent-amber-light)'
          : // --text-cream-faint (0.35) stands for the artboard's 0.32 edge, which has no token.
            'border-(--text-cream-faint) bg-transparent text-(--text-cream) not-aria-disabled:hover:bg-(--text-cream-hairline)'
      } ${busy && !saving ? 'opacity-60' : ''}`}
      style={{ fontFamily: 'var(--font-body)' }}
    >
      {saving ? (
        <span className="inline-flex items-center justify-center">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          <span className="sr-only">{label}</span>
        </span>
      ) : (
        label
      )}
    </button>
  )
}
